import { useState } from 'react';
import dayjs from 'dayjs';
import { Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

import { downloadFile } from 'src/utils/file';
import env from 'env';
import { Separator } from '@/components/ui/separator';
import { cn } from 'src/lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

export type Props = {
  approvedBy: {
    beneficiary: boolean;
    codekeeper: boolean;
    depositor: boolean;
  };
  updatedAt?: Date;
  download: {
    docxUrl?: string;
    pdfUrl?: string;
    signedPdfUrl?: string;
  };
  fileName: string;
  isTripartite: boolean;
  canDownload?: boolean;
  className?: string;
};

const GeneratedAgreementSidebar = ({
  className,
  approvedBy,
  updatedAt,
  download,
  fileName,
  isTripartite,
  canDownload = false,
}: Props) => {
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [isLoadingDocx, setIsLoadingDocx] = useState(false);
  const [isLoadingSignedPdf, setIsLoadingSignedPdf] = useState(false);
  const approvalItems = [
    {
      title: 'Codekeeper',
      isApproved: approvedBy.codekeeper,
    },
    {
      title: 'Depositor',
      isApproved: approvedBy.depositor,
    },
    ...(isTripartite
      ? [
          {
            title: 'Beneficiary',
            isApproved: approvedBy.beneficiary,
          },
        ]
      : []),
  ];

  const onDownloadPdf = async () => {
    if (!download.pdfUrl) return;

    setIsLoadingPdf(true);
    try {
      await downloadFile(download.pdfUrl, 'pdf', fileName);
    } catch (e: unknown) {
      if (e instanceof Error) toast.error(e.message || 'Something went wrong');
    } finally {
      setIsLoadingPdf(false);
    }
  };

  const onDownloadDocx = async () => {
    if (!download.docxUrl) return;

    setIsLoadingDocx(true);
    try {
      await downloadFile(download.docxUrl, 'docx', fileName);
    } catch (e: unknown) {
      if (e instanceof Error) toast.error(e.message || 'Something went wrong');
    } finally {
      setIsLoadingDocx(false);
    }
  };

  const onDownloadSignedPdf = async () => {
    if (!download.signedPdfUrl) return;

    setIsLoadingSignedPdf(true);
    try {
      await downloadFile(download.signedPdfUrl, 'pdf', fileName);
    } catch (e: unknown) {
      if (e instanceof Error) toast.error(e.message || 'Something went wrong');
    } finally {
      setIsLoadingSignedPdf(false);
    }
  };

  return (
    <aside className={cn('p-6 bg-white shadow-sm border-border rounded-lg space-y-6', className)}>
      <div className="text-muted-foreground text-sm">
        Last updated: <br className="hidden md:block" />
        {updatedAt && <span>{dayjs(updatedAt).format('DD MMMM YYYY, hh:mmA')}</span>}
      </div>
      <Separator />

      <ul className="space-y-4">
        {approvalItems.map(({ title, isApproved }) => {
          return (
            <li key={title} className="pl-5 relative text-sm space-y-2">
              <div className="absolute left-0 top-[3px]">
                <span
                  className={cn(
                    'size-3.5 rounded-full bg-input flex items-center justify-center',
                    isApproved && 'bg-secondary-foreground',
                  )}
                >
                  {isApproved && <Check className="size-2.5 text-white" />}
                </span>
              </div>
              <div className={cn('font-semibold text-muted-foreground text-sm', isApproved && 'text-foreground')}>
                {title}
              </div>
              <div className="text-muted-foreground text-sm">Approved this version</div>
            </li>
          );
        })}
      </ul>
      {download.signedPdfUrl && (
        <Button
          variant="outline"
          isLoading={isLoadingSignedPdf}
          onClick={onDownloadSignedPdf}
          disabled={!canDownload || isLoadingSignedPdf}
          className="w-full"
        >
          Download Signed PDF
        </Button>
      )}
      <Separator />

      <div className="space-y-1.5">
        <strong className="block text-foreground font-bold text-base">Need specific terms?</strong>
        <p className="text-muted-foreground text-sm">
          Download the agreement using the link below, and send us a marked up version at{' '}
          <a className="text-foreground! hover:underline!" href={`mailto: ${env.CODEKEEPER_SERVICE_EMAIL}`}>
            {env.CODEKEEPER_SERVICE_EMAIL}
          </a>
          <br />
          <br />
          Make sure to keep ‘track changes’ on when changing the agreement content.
        </p>
      </div>

      <DropdownMenu>
        <div className="flex justify-center">
          <DropdownMenuTrigger asChild disabled={!canDownload}>
            <Button variant="outline" isLoading={isLoadingPdf || isLoadingDocx} data-testid="download-button">
              Download <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
        </div>
        <DropdownMenuContent side="right" align="start" className="w-max">
          <DropdownMenuItem className="py-2 cursor-pointer" disabled={!download.docxUrl} onClick={onDownloadDocx}>
            Download .docx
          </DropdownMenuItem>
          <DropdownMenuItem className="py-2 cursor-pointer" disabled={!download.pdfUrl} onClick={onDownloadPdf}>
            Download PDF
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </aside>
  );
};

export default GeneratedAgreementSidebar;
